import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { WINDOW_TOKEN } from '../../../services/bootstrap.service';
import type { OnDestroy } from '@angular/core';
import type { Observable, Subscription } from 'rxjs';
import type { AccessibleErrors } from '../domain/form.domain';

/**
 * A gatekeeper service that aggregates form errors from potentially nested forms and provides them to the
 * first form-accessibility-alert component that registers itself with it.
 * */
@Injectable()
export class FormAccessibilityService implements OnDestroy {
  get shouldSubscribeToModalAlerts(): boolean {
    return this._shouldSubscribeToModalAlerts;
  }

  set shouldSubscribeToModalAlerts(value: boolean) {
    this._shouldSubscribeToModalAlerts = value;
  }

  get shouldSubscribeToPageAlerts(): boolean {
    return this._shouldSubscribeToPageAlerts;
  }

  set shouldSubscribeToPageAlerts(value: boolean) {
    this._shouldSubscribeToPageAlerts = value;
  }

  static readonly ERROR_KEY_SUFFIX: string = '-accessible-error';

  private modalAlertSubscription: Subscription;
  private pageAlertSubscription: Subscription;

  private pageAccessibleErrors: Array<Observable<AccessibleErrors>> = [];
  private pageAlertSubject: BehaviorSubject<AccessibleErrors> = new BehaviorSubject<AccessibleErrors>({});
  private readonly destroyPageAlert: Subject<void> = new Subject<void>();
  private currentPageErrors: AccessibleErrors;

  private modalAccessibleErrors: Array<Observable<AccessibleErrors>> = [];
  private modalAlertSubject: BehaviorSubject<AccessibleErrors> = new BehaviorSubject<AccessibleErrors>({});
  private readonly destroyModalAlert: Subject<void> = new Subject<void>();
  private currentModalErrors: AccessibleErrors;

  private _shouldSubscribeToPageAlerts: boolean = true;
  private _shouldSubscribeToModalAlerts: boolean = true;

  constructor(@Inject(WINDOW_TOKEN) private browserWindow: Window) {
  }

  static getErrorKey(inputName: string): string {
    return inputName + FormAccessibilityService.ERROR_KEY_SUFFIX;
  }

  isModal(): boolean {
    return this.browserWindow.document.querySelector('td-wb-modal') !== null;
  }

  register(isModalAlert: boolean): Observable<AccessibleErrors> {
    if (isModalAlert && this.shouldSubscribeToModalAlerts) {
      this.shouldSubscribeToModalAlerts = false;
      return this.modalAlertSubject;
    } else if (this.shouldSubscribeToPageAlerts) {
      this.shouldSubscribeToPageAlerts = false;
      return this.pageAlertSubject;
    }
  }

  registerErrors(errors: Observable<AccessibleErrors>, isModalForm: boolean = false): void {
    if (isModalForm) {
      this.handleModalErrors(errors);
    } else {
      this.handlePageErrors(errors);
    }
  }

  unregisterErrors(isModalForm: boolean): void {
    if (isModalForm && this.modalAlertSubscription !== undefined) {
      this.modalAlertSubscription.unsubscribe();
      this.modalAccessibleErrors = [];
    } else if (this.pageAlertSubscription !== undefined) {
      this.pageAlertSubscription.unsubscribe();
      this.pageAccessibleErrors = [];
    }
  }

  getVisibleErrorCount(isModal: boolean): number {
    let errorCount = 0;
    if (isModal) {
      for (const error in this.currentModalErrors) {
        if (this.currentModalErrors[error].isDisplayed) {
          errorCount++;
        }
      }
    } else {
      for (const error in this.currentPageErrors) {
        if (this.currentPageErrors[error].isDisplayed) {
          errorCount++;
        }
      }
    }
    return errorCount;
  }

  scrollErrorIntoView(isModalForm: boolean = false): void {
    if (isModalForm && this.currentModalErrors !== undefined) {
      const errorKey = Object.keys(this.currentModalErrors)[0];
      const error = this.currentModalErrors[errorKey];
      if (error !== undefined) {
        if (!error.isInViewPort()) {
          error.scrollIntoView();
        } else {
          error.focusInput();
        }
      }
    } else if (this.currentPageErrors !== undefined) {
      const errorKey = Object.keys(this.currentPageErrors)[0];
      const error = this.currentPageErrors[errorKey];
      if (error !== undefined) {
        if (!error.isInViewPort()) {
          error.scrollIntoView();
        } else {
          error.focusInput();
        }
      }
    }

  }

  ngOnDestroy(): void {
    this.destroyPageAlert.next();
    this.destroyPageAlert.complete();
    this.destroyModalAlert.next();
    this.destroyModalAlert.complete();
  }

  private handlePageErrors(errors: Observable<AccessibleErrors>): void {
    this.pageAccessibleErrors.push(errors);
    this.pageAlertSubscription = combineLatest([...this.pageAccessibleErrors])
      .pipe(
        takeUntil(this.destroyPageAlert),
        debounceTime(100))
      .subscribe((value: Array<AccessibleErrors>) => {
        const currentErrors = JSON.stringify(this.currentPageErrors);
        const incomingErrors = JSON.stringify(Object.assign({}, ...value));

        this.currentPageErrors = Object.assign({}, ...value);
        if (currentErrors !== incomingErrors) {
          this.pageAlertSubject.next(Object.assign({}, ...value));
        }
      });
  }

  private handleModalErrors(errors: Observable<AccessibleErrors>): void {
    this.modalAccessibleErrors.push(errors);
    this.modalAlertSubscription = combineLatest([...this.modalAccessibleErrors])
      .pipe(
        takeUntil(this.destroyModalAlert),
        debounceTime(100))
      .subscribe((value: Array<AccessibleErrors>) => {
        const currentErrors = JSON.stringify(this.currentModalErrors);
        const incomingErrors = JSON.stringify(Object.assign({}, ...value));

        this.currentModalErrors = Object.assign({}, ...value);
        if (currentErrors !== incomingErrors) {
          this.modalAlertSubject.next(Object.assign({}, ...value));
        }
      });
  }
}
